import React, { useEffect, useState } from "react";
import "./style.css";
import { ApiCallStatus } from "../../utils/constants";
import { fetchProductsData } from "../../main-component/PageFunctions/productFunctions";
import { fetchProductStockInfo } from "../../main-component/PageFunctions/priceFunctions";
import { getProductBySku } from "../../api/productController";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import { chain, find, includes, map, slice, toLower, trim } from "lodash";
import GridItem from "../GridItem";
import { COLORS } from "../../constants";
import useSystemHook from "./../../hooks/SystemHook";

const FindProductsByCategory = ({ categoriesAndTypes, customerCategory }) => {
  const { handleAddToCart } = useArgusCart();

  const [products, setProducts] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);

  const [productCategoryLink, setProductCategoryLink] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [quantities, setQuantities] = useState(null);

  const [productsCallStatus, setProductsCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [detailsCallStatus, setDetailsCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [pricesCallStatus, setPricesCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [stockInfoCallStatus, setStockInfoCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );

  const { isMobile, isTablet, isDesktop } = useSystemHook();
  const fetchProductsDataAsync = async (category) => {
    await fetchProductsData({
      productCategory: category,
      pageNumber: 0,
      limit: 6,
      setProducts,
      setAllProductIds: () => {},
      setTotal: () => {},
      setPageSize: () => {},
      setPageNumber: () => {},
      setStatusInProgress: () =>
        setProductsCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () => setProductsCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setProductsCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setProductsCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchProductStockInfoAsync = async () => {
    const productSkus = map(products, (x) => x.sku);
    await fetchProductStockInfo({
      productSkus,
      setPricingData,
      setQuantities,
      setStatusInProgress: () =>
        setStockInfoCallStatus(ApiCallStatus.InProgress),
      setStatusSuccess: () => setStockInfoCallStatus(ApiCallStatus.Succeeded),
      setStatusFailed: () => setStockInfoCallStatus(ApiCallStatus.Failed),
      setStatusError: () => setStockInfoCallStatus(ApiCallStatus.Error),
    });
  };

  const fetchDetailedProductsAsync = async (productList) => {
    setDetailsCallStatus(ApiCallStatus.InProgress);
    try {
      const detailedResults = await Promise.all(
        productList.map(async (product) => {
          const response = await getProductBySku({ sku: product.sku });
          return response?.success ? response.data : null;
        })
      );
      setProducts(
        productList.map((product, i) => {
          const detail = detailedResults[i];
          return detail ? { ...product, ...detail } : product;
        })
      );
      setDetailsCallStatus(ApiCallStatus.Succeeded);
    } catch (error) {
      console.error(error);
      setDetailsCallStatus(ApiCallStatus.Failed);
    }
  };

  const handleAddToCartClick = (product) => {
    handleAddToCart({
      id: product.productId,
      name: product.name,
      vendor: product.vendor,
      image: product.image,
      sku: product.sku,
    });
  };

  const scrollTop = () => {
    window.scrollTo(0, 0);
  };

  const handleActiveCategoryChange = (category) => {
    setActiveCategory(category);
  };

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    if (!!activeCategory) {
      const categorySlug = trim(toLower(activeCategory.replaceAll(" ", "_")));

      setProductCategoryLink(encodeURI(`/products/${categorySlug}`));
      fetchProductsDataAsync(categorySlug);
    } else if (!activeCategory) {
      setActiveCategory("Service parts");
    }
  }, [activeCategory]);

  useEffect(() => {
    if (productsCallStatus === ApiCallStatus.InProgress) {
      setDetailsCallStatus(ApiCallStatus.NotStarted);
    }
  }, [productsCallStatus]);

  useEffect(() => {
    if (
      productsCallStatus === ApiCallStatus.Succeeded &&
      !!products &&
      detailsCallStatus === ApiCallStatus.NotStarted
    ) {
      fetchDetailedProductsAsync(products);
      fetchProductStockInfoAsync();
    }
  }, [products, productsCallStatus, detailsCallStatus]);

  const allLoadingStates = [
    productsCallStatus,
    detailsCallStatus,
    pricesCallStatus,
    stockInfoCallStatus,
  ];

  const loading = includes(allLoadingStates, ApiCallStatus.InProgress);

  return (
    <Container maxWidth="lg" disableGutters>
      {/* <Box sx={{ mt: 4, p: 2 }}>
				<Typography variant="h4" gutterBottom sx={{ textAlign: "center" }}>
					Find Part by Category
				</Typography>

				<Box sx={{ height: '32px' }} />
			</Box> */}

      {/* <CategorySlider
				categoriesAndTypes={categoriesAndTypes}
				handleActiveCategoryChange={handleActiveCategoryChange}
				activeCategory={activeCategory}
			/> */}

      <Box sx={{ mt: 5, p: 2, mb: isMobile ? 0 : 4 }}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ flexWrap: "nowrap" }}
        >
          <Grid item xs={6} sm={6}>
            <Typography
              variant={!isMobile ? "h6" : ""}
              fontFamily="Rubik"
              gutterBottom
              color={COLORS.darkText}
              sx={{ display: "inline", textTransform: "uppercase" }}
            >
              <span style={{ fontWeight: 500 }}>Service</span>{" "}
              <span style={{ fontWeight: 200 }}>Parts</span>
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} sx={{ textAlign: "right" }}>
            <Button
              color="error"
              size="small"
              variant="contained"
              href={productCategoryLink}
              sx={{
                mt: 0,
                borderRadius: "2px",
                backgroundColor: "transparent",
                border: `1px solid ${COLORS.themeRed}`,
                color: COLORS.themeRed,
                fontFamily: "Rubik",
                fontWeight: 500,
                textTransform: "uppercase",
                padding: "0.5rem 2rem",
                "&:hover": {
                  backgroundColor: COLORS.themeRed,
                  color: COLORS.white,
                  borderColor: COLORS.themeRed,
                },
                boxShadow: "none",
                "&:focus": {
                  boxShadow: "none",
                },
              }}
            >
              View all
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ height: "32px" }} />

        <Box sx={{ px: 0 }}>
          <Grid container spacing={0}>
            {!!products &&
              !!pricingData &&
              map(slice(products, 0, 6), (product, itemNumber) => {
                const priceObject = find(
                  pricingData,
                  (p) => p.sku === product.sku,
                );
                const stockInfoObject = find(
                  quantities,
                  (p) => p.sku === product.sku,
                );
                const promoPrice = priceObject?.promo ?? 0;
                const promoInvoicePrice = priceObject?.promoInvoicePrice ?? 0;

                let itemPrice = priceObject?.price ?? 0;
                let invoicePrice = priceObject?.invoicePrice ?? 0;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={isTablet ? 6 : 4}
                    key={itemNumber}
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "center", // Centered on mobile (xs)
                        sm:
                          itemNumber % `${isTablet ? 2 === 1 : 3 === 1}`
                            ? "center"
                            : itemNumber % `${isTablet ? 2 === 1 : 3 === 1}`
                              ? "flex-start"
                              : "flex-end",
                      },
                      padding: "0.3rem 0",
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: "100%", sm: "97%" },
                        maxWidth: "22rem",
                      }}
                    >
                      <GridItem
                        key={itemNumber}
                        title={product.name}
                        price={itemPrice}
                        invoicePrice={invoicePrice}
                        promo={promoPrice}
                        promoInvoicePrice={promoInvoicePrice}
                        customerCategory={customerCategory}
                        vendor={product.vendor}
                        image={product.image}
                        productId={product.productId}
                        vendorImage={product.vendorImage}
                        handle={product.handle}
                        vendorDescription={product.vendorDescription}
                        quality={product.quality}
                        fitment={product.fitment}
                        availability={
                          stockInfoObject?.quantityOnHand ??
                          product.availability
                        }
                        warranty={product.warranty}
                        warrantyText={product.warrantyText}
                        applicationPosition={product.applicationPosition}
                        applicationPositionImage={
                          product.applicationPositionImage
                        }
                        sku={product.sku}
                        addToCart={() => handleAddToCart(product)}
                      />
                    </Box>
                  </Grid>
                );
              })}
          </Grid>
        </Box>

        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Container>
    // <section>
    // 	{!!categoriesAndTypes && !selectedCategory && (
    // 		<article>
    // 			<Swiper
    // 				modules={[Autoplay, Navigation]}
    // 				spaceBetween={isDesktopOrLaptop ? 70 : 0}
    // 				slidesPerView={isDesktopOrLaptop ? 1 : 4}
    // 				navigation
    // 				loop={true}
    // 				pagination={{clickable: true}}
    // 				scrollbar={{draggable: true}}
    // 				autoplay={{
    // 					delay: 5000,
    // 					disableOnInteraction: true
    // 				}}
    // 			>
    // 				{categoriesAndTypes.map((category, index) => {
    // 					const categoryName = capitalize(
    // 						category.category.replaceAll("_", " ").toLowerCase()
    // 					);
    //
    // 					return (
    // 						<SwiperSlide key={index}>
    // 							<article>
    // 								<Link
    // 									to="#"
    // 									onClick={() => handleActiveCategoryChange(categoryName)}
    // 									className="category_container"
    // 								>
    // 									<h1 key={index} className="product_category_heading">
    // 										{categoryName}
    // 									</h1>
    //
    // 								</Link>
    // 							</article>
    // 						</SwiperSlide>
    // 					);
    // 				})}
    // 			</Swiper>
    // 		</article>
    // 	)}
    // 	{!!products && !!pricingData &&
    // 		<Grid container>
    // 			<Grid.Row>
    // 				<Grid.Column width={8}>
    // 					<h1 className="products_home_heading">{activeCategory}</h1>
    // 				</Grid.Column>
    // 				<Grid.Column width={8} textAlign="right">
    // 					<Button
    // 						as={Link}
    // 						className="red-button"
    // 						to={productCategoryLink}
    // 					>
    // 						View all products
    // 					</Button>
    // 				</Grid.Column>
    // 			</Grid.Row>
    // 			{/*<Grid.Row>*/}
    // 			{/*	<Grid.Column width={16}>*/}
    // 			{/*		<Card.Group itemsPerRow={isDesktopOrLaptop ? 1 : 5}>*/}
    // 			{/*			{products.map((product) => {*/}
    // 			{/*				const priceObject = find(pricingData, p => p.sku === product.sku);*/}
    // 			{/*				const itemPrice = priceObject?.price ?? 0;*/}
    // 			{/*				const invoicePrice = priceObject?.invoicePrice ?? 0;*/}
    // 			{/*				return { product, itemPrice, invoicePrice };*/}
    // 			{/*			})*/}
    // 			{/*				.sort((a, b) => a.product.globalRanking - b.product.globalRanking)*/}
    // 			{/*				.slice(0, 5)*/}
    // 			{/*				.map(({ product, itemPrice, invoicePrice }, index) => (*/}
    // 			{/*					<GridItem*/}
    // 			{/*						key={index}*/}
    // 			{/*						title={product.name}*/}
    // 			{/*						price={itemPrice}*/}
    // 			{/*						invoicePrice={invoicePrice}*/}
    // 			{/*						customerCategory={customerCategory}*/}
    // 			{/*						vendor={product.vendor}*/}
    // 			{/*						image={product.image}*/}
    // 			{/*						productId={product.productId}*/}
    // 			{/*						vendorImage={product.vendorImage}*/}
    // 			{/*						handle={product.handle}*/}
    // 			{/*						vendorDescription={product.vendorDescription}*/}
    // 			{/*						quality={product.quality}*/}
    // 			{/*						fitment={product.fitment}*/}
    // 			{/*						availability={product.availability}*/}
    // 			{/*						warranty={product.warranty}*/}
    // 			{/*						warrantyText={product.warrantyText}*/}
    // 			{/*						applicationPosition={product.applicationPosition}*/}
    // 			{/*						applicationPositionImage={product.applicationPositionImage}*/}
    // 			{/*						bodyHtml={product.html}*/}
    // 			{/*						sku={product.sku}*/}
    // 			{/*						addToCart={handleAddToCart}*/}
    // 			{/*					/>*/}
    // 			{/*				))*/}
    // 			{/*			}*/}
    // 			{/*		</Card.Group>*/}
    //
    // 			{/*	</Grid.Column>*/}
    // 			{/*</Grid.Row>*/}
    // 		</Grid>
    // 	}
    // 	{!!productAddedInformation && (
    // 		<ConfirmationModal
    // 			open={!!productAddedInformation}
    // 			confirmLabel="Go to cart"
    // 			rejectLabel="Continue shopping"
    // 			heading="Product added"
    // 			closeModal={handleProductAddedModalClose}
    // 			content={
    // 				<Grid container>
    // 					<Grid.Row>
    // 						<Grid.Column width={16}>
    // 							<p className="h6">{productAddedInformation.name}</p>
    // 						</Grid.Column>
    // 					</Grid.Row>
    // 					<Grid.Row>
    // 						<Grid.Column width={16}>
    // 							<Image src={productAddedInformation.image}/>
    // 						</Grid.Column>
    // 					</Grid.Row>
    // 				</Grid>
    // 			}
    // 		/>
    // 	)}
    // 	<Backdrop
    // 		sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
    // 		open={pageIsLoading}
    // 	>
    // 		<CircularProgress color="inherit"/>
    // 	</Backdrop>
    // </section>
  );
};

export default FindProductsByCategory;
